import React, { createContext, useContext, useState, useEffect } from 'react';
import NetworkErrorModal from '../components/NetworkErrorModal/NetworkErrorModal';

const NetworkContext = createContext();

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};

export const NetworkProvider = ({ children }) => {

  const [isOnline, setIsOnline] = useState(() => {
    try {
      return navigator.onLine !== undefined ? navigator.onLine : true;
    } catch (error) {
      return true; // Default to online if navigator is not available
    }
  });
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [networkErrorCount, setNetworkErrorCount] = useState(0);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowNetworkError(false);
      setNetworkErrorCount(0);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowNetworkError(true);
      setNetworkErrorCount(prev => prev + 1);
    };

    const handleNetworkError = (event) => {
      // Handle custom network error events from video player or other components
      setShowNetworkError(true);
      setNetworkErrorCount(prev => prev + 1);
    };

    try {
      // Listen for network status changes
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      window.addEventListener('networkError', handleNetworkError);

      // Also check for network errors periodically
      const checkNetworkStatus = () => {
        try {
          if (navigator.onLine !== undefined && !navigator.onLine && !showNetworkError) {
            setShowNetworkError(true);
            setNetworkErrorCount(prev => prev + 1);
          }
        } catch (error) {
          // console.log(error)
        }
      };

      const networkCheckInterval = setInterval(checkNetworkStatus, 5000);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        window.removeEventListener('networkError', handleNetworkError);
        clearInterval(networkCheckInterval);
      };
    } catch (error) {
      // If event listeners fail, just return empty cleanup
      return () => {};
    }
  }, [showNetworkError]);

  // No button handlers needed - modal is just informational

  // Function to manually trigger network error (for testing or specific cases)
  const triggerNetworkError = () => {
    try {
      setShowNetworkError(true);
      setNetworkErrorCount(prev => prev + 1);
    } catch (error) {
      // Silently handle any errors
    }
  };

  const value = {
    isOnline,
    showNetworkError,
    networkErrorCount,
    triggerNetworkError,
    setShowNetworkError
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
      <NetworkErrorModal
        isModalOpen={showNetworkError}
      />
    </NetworkContext.Provider>
  );
};
